import { useState } from "react";
import { Loader2, X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EditableChip } from "./EditableChip";
import { CategoryChip } from "./CategoryChip";
import { CurrencyChip } from "./CurrencyChip";
import { useLanguage } from "@/contexts/LanguageContext";
import { getCategoryIcon } from "@/lib/categoryIcons";

export interface PendingExpenseItem {
  id: string;
  amount: number;
  currency: string;
  merchant: string;
  category: string;
  note?: string;
  date: string;
}

interface BatchConfirmationCardProps {
  expenses: PendingExpenseItem[];
  onConfirmAll: () => void;
  onCancel: () => void;
  onUpdateExpense: (id: string, updates: Partial<PendingExpenseItem>) => void;
  onRemoveExpense: (id: string) => void;
  isProcessing: boolean;
}

export const BatchConfirmationCard = ({
  expenses,
  onConfirmAll,
  onCancel,
  onUpdateExpense,
  onRemoveExpense,
  isProcessing,
}: BatchConfirmationCardProps) => {
  const { t } = useLanguage();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const isBatch = expenses.length > 1;
  const total = expenses.reduce((sum, exp) => sum + exp.amount, 0);
  const currency = expenses[0]?.currency || 'EUR';

  if (expenses.length === 0) return null;

  // Single expense - show compact inline editor
  if (!isBatch) {
    const expense = expenses[0];
    return (
      <div className="sticky bottom-[80px] mb-2 animate-slide-up z-10">
        <div className="bg-card border-2 border-primary/30 rounded-2xl p-5 shadow-xl backdrop-blur-sm space-y-4">
          <div className="flex flex-col gap-3">
            {/* Editable Amount + Currency */}
            <div className="flex items-center gap-1">
              <EditableChip
                value={expense.amount.toString()}
                onChange={(val) => {
                  const num = parseFloat(val);
                  if (!isNaN(num) && num > 0) {
                    onUpdateExpense(expense.id, { amount: num });
                  }
                }}
                type="number"
                label={t('chat.editAmount')}
                className="text-xl font-bold"
              />
              <CurrencyChip
                value={expense.currency}
                onChange={(cur) => onUpdateExpense(expense.id, { currency: cur })}
              />
            </div>
            
            {/* Editable Merchant & Category */}
            <div className="flex flex-wrap items-center gap-2">
              <EditableChip
                value={expense.merchant}
                onChange={(val) => onUpdateExpense(expense.id, { merchant: val })}
                label={t('chat.editMerchant')}
              />
              <span className="text-muted-foreground">•</span>
              <CategoryChip
                value={expense.category}
                onChange={(cat) => onUpdateExpense(expense.id, { category: cat })}
              />
            </div>
            
            {expense.note && (
              <p className="text-sm text-muted-foreground italic">{expense.note}</p>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              onClick={onConfirmAll}
              disabled={isProcessing}
              className="flex-1 min-h-[48px] text-base"
            >
              {isProcessing ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : null}
              {t('common.confirm') || 'Confirm'}
            </Button>
            <Button
              onClick={onCancel}
              disabled={isProcessing}
              variant="outline"
              className="min-h-[48px] text-base"
            >
              {t('common.cancel') || 'Cancel'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Multi-expense batch UI
  return (
    <div className="sticky bottom-[80px] mb-2 animate-slide-up z-10">
      <div className="bg-card border-2 border-primary/30 rounded-2xl p-4 shadow-xl backdrop-blur-sm space-y-3">
        {/* Header with count */}
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-base">
            {t('chat.saveExpenses', { count: expenses.length }) || `Save ${expenses.length} expenses?`}
          </h3>
          <span className="text-sm text-muted-foreground font-medium">
            {t('chat.batchTotal')}: {currency} {total.toFixed(2)}
          </span>
        </div>

        {/* Expense List */}
        <div className="space-y-2 max-h-[200px] overflow-y-auto">
          {expenses.map((expense) => {
            const isExpanded = expandedId === expense.id;
            const emoji = getCategoryIcon(expense.category);

            return (
              <div
                key={expense.id}
                className="bg-muted/50 rounded-xl p-3 space-y-2"
              >
                {/* Collapsed row */}
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setExpandedId(isExpanded ? null : expense.id)}
                    className="flex items-center gap-2 flex-1 text-left"
                  >
                    <span className="text-lg">{emoji}</span>
                    <span className="font-medium truncate max-w-[120px]">
                      {expense.merchant}
                    </span>
                    <span className="text-muted-foreground">
                      {expense.currency} {expense.amount.toFixed(2)}
                    </span>
                  </button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onRemoveExpense(expense.id)}
                    className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                    disabled={isProcessing}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>

                {/* Expanded editor */}
                {isExpanded && (
                  <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border/50">
                    <EditableChip
                      value={expense.amount.toString()}
                      onChange={(val) => {
                        const num = parseFloat(val);
                        if (!isNaN(num) && num > 0) {
                          onUpdateExpense(expense.id, { amount: num });
                        }
                      }}
                      type="number"
                      label={t('chat.editAmount')}
                      className="font-semibold"
                    />
                    <CurrencyChip
                      value={expense.currency}
                      onChange={(cur) => onUpdateExpense(expense.id, { currency: cur })}
                    />
                    <EditableChip
                      value={expense.merchant}
                      onChange={(val) => onUpdateExpense(expense.id, { merchant: val })}
                      label={t('chat.editMerchant')}
                    />
                    <CategoryChip
                      value={expense.category}
                      onChange={(cat) => onUpdateExpense(expense.id, { category: cat })}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 pt-1">
          <Button
            onClick={onConfirmAll}
            disabled={isProcessing || expenses.length === 0}
            className="flex-1 min-h-[48px] text-base gap-2"
          >
            {isProcessing ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Check className="w-5 h-5" />
            )}
            {t('chat.saveAll') || 'Save All'}
          </Button>
          <Button
            onClick={onCancel}
            disabled={isProcessing}
            variant="outline"
            className="min-h-[48px] text-base"
          >
            {t('common.cancel') || 'Cancel'}
          </Button>
        </div>
      </div>
    </div>
  );
};
